// src/pages/admin/UserManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit, Trash2, Lock, Unlock, RotateCcw,
  Shield, Loader2, Users, KeyRound, X, Mail, Building2,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Select } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { User, Role, Unit } from '../../types';

const flattenUnits = (units: any[], level: number = 0): { value: string; label: string }[] => {
  const result: { value: string; label: string }[] = [];
  const indent = '\u00A0\u00A0'.repeat(level);
  const prefix = level > 0 ? '└─ ' : '';
  for (const unit of units) {
    result.push({ value: unit.id.toString(), label: `${indent}${prefix}${unit.name}` });
    if (unit.children_recursive && unit.children_recursive.length > 0) {
      result.push(...flattenUnits(unit.children_recursive, level + 1));
    }
  }
  return result;
};

// Role → color palette for badges and avatars
const roleColorMap: Record<string, { bg: string; text: string; border: string; avatar: string }> = {
  admin: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', avatar: 'bg-rose-500' },
  rektor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', avatar: 'bg-amber-500' },
  wakil_rektor_1: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  wakil_rektor_2: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  wakil_rektor_3: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  wakil_rektor_4: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', avatar: 'bg-indigo-500' },
  dekan: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', avatar: 'bg-blue-500' },
  kaprodi: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', avatar: 'bg-purple-500' },
  kalem: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', avatar: 'bg-teal-600' },
  kabag: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', avatar: 'bg-orange-500' },
  ka_tu: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', avatar: 'bg-cyan-600' },
  pic: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', avatar: 'bg-emerald-500' },
  lpm: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', avatar: 'bg-teal-700' },
  senat: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', avatar: 'bg-slate-500' },
};

const getRoleColor = (roleName?: string) =>
  roleColorMap[roleName || ''] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', avatar: 'bg-gray-500' };

const UserManagement: React.FC = () => {
  const PER_PAGE = 12;
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [flatUnitOptions, setFlatUnitOptions] = useState<{ value: string; label: string }[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [editUser, setEditUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role_id: '', unit_id: '' });
  const [saving, setSaving] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [rolesRes, unitsRes] = await Promise.all([adminService.roles.getAll(), adminService.units.getAll()]);
        const rolesData = (rolesRes as any)?.data || rolesRes;
        const unitsData = (unitsRes as any)?.data || unitsRes;
        setRoles(Array.isArray(rolesData) ? rolesData : []);
        setFlatUnitOptions(flattenUnits(Array.isArray(unitsData) ? unitsData : []));
      } catch (e) {
        toast.error('Gagal memuat data master');
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => { setCurrentPage(1); }, [search, roleFilter]);
  useEffect(() => { fetchUsers(); }, [currentPage, search, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [res, activeRes] = await Promise.all([
        adminService.users.getAll({ page: currentPage, per_page: PER_PAGE, search, role_id: roleFilter ? Number(roleFilter) : undefined }),
        adminService.users.getAll({ page: 1, per_page: 1, is_active: true })
      ]);
      
      const payload = (res as any)?.data || {};
      const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      setUsers(rows);
      setTotalPages(Number(payload?.last_page || 1));
      setTotalUsers(Number(payload?.total || rows.length));
      
      const activePayload = (activeRes as any)?.data || {};
      setTotalActiveUsers(Number(activePayload?.total || 0));
    } catch (e) {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditUser(null);
    setFormData({ name: '', email: '', password: '', role_id: '', unit_id: '' });
    setShowModal(true);
  };

  const openEdit = (user: User) => {
    setEditUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role_id: user.role?.id.toString() || '', unit_id: user.unit?.id.toString() || '' });
    setShowModal(true);
  };

  const openReset = (user: User) => {
    setResetTarget(user);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email || !formData.role_id) { toast.error('Nama, email, dan role wajib diisi'); return; }
    if (!editUser && !formData.password) { toast.error('Password wajib diisi untuk user baru'); return; }
    setSaving(true);
    try {
      const payload: any = { name: formData.name, email: formData.email, role_id: Number(formData.role_id), unit_id: formData.unit_id ? Number(formData.unit_id) : undefined };
      if (formData.password) payload.password = formData.password;
      if (editUser) { await adminService.users.update(editUser.id, payload); toast.success('User berhasil diperbarui'); }
      else { await adminService.users.create(payload); toast.success('User berhasil ditambahkan'); }
      setShowModal(false);
      fetchUsers();
    } catch (e) { toast.error('Gagal menyimpan user'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Yakin ingin menghapus user ini?')) {
      try { await adminService.users.delete(id); toast.success('User berhasil dihapus'); fetchUsers(); }
      catch (e) { toast.error('Gagal menghapus user'); }
    }
  };

  const handleToggleActive = async (user: User) => {
    try { await adminService.users.toggleActive(user.id); toast.success(`User ${user.is_active ? 'dinonaktifkan' : 'diaktifkan'}`); fetchUsers(); }
    catch (e) { toast.error('Gagal mengubah status user'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) { toast.error('Password minimal 8 karakter'); return; }
    setResetSaving(true);
    try {
      await adminService.users.resetPassword(resetTarget!.id, newPassword);
      toast.success('Password berhasil direset');
      setShowResetModal(false);
    } catch (e) { toast.error('Gagal reset password'); }
    finally { setResetSaving(false); }
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Manajemen User' }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Manajemen User</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <span className="font-bold text-teal-main">{totalUsers}</span> pengguna terdaftar
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>
          Tambah User
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total User', value: totalUsers, icon: <Users className="w-5 h-5" />, color: 'text-teal-700 bg-teal-50 border-teal-200' },
          { label: 'Aktif', value: totalActiveUsers, icon: <Unlock className="w-5 h-5" />, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
          { label: 'Nonaktif', value: Math.max(0, totalUsers - totalActiveUsers), icon: <Lock className="w-5 h-5" />, color: 'text-rose-700 bg-rose-50 border-rose-200' },
          { label: 'Role', value: roles.length, icon: <Shield className="w-5 h-5" />, color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
        ].map(stat => (
          <div key={stat.label} className={`relative overflow-hidden rounded-xl border p-4 flex items-center gap-3 transition-all hover:shadow-md ${stat.color}`}>
            <div className="p-2 rounded-lg bg-white/70 shadow-sm">{stat.icon}</div>
            <div>
              <p className="text-2xl font-black">{stat.value}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide opacity-70">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
          <input
            type="text"
            placeholder="Cari nama atau email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-neutral-border text-sm bg-white focus:outline-none focus:border-teal-main shadow-sm transition-colors"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-3 rounded-xl border-2 border-neutral-border text-sm bg-white focus:outline-none focus:border-teal-main shadow-sm min-w-[180px] transition-colors"
        >
          <option value="">Semua Role</option>
          {roles.map(r => <option key={r.id} value={r.id.toString()}>{r.display_name}</option>)}
        </select>
      </div>

      {/* User Table */}
      <Card padding="sm" className="!p-0 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-teal-main" />
            <p className="text-sm text-neutral-text-secondary">Memuat data pengguna...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mx-auto mb-3">
              <Users className="w-8 h-8 text-neutral-text-tertiary" />
            </div>
            <p className="text-base font-bold text-neutral-text-primary">Tidak ada pengguna ditemukan</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {search || roleFilter ? 'Coba ubah filter pencarian.' : 'Tambahkan pengguna pertama.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-neutral-bg border-b border-neutral-border">
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Pengguna</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary hidden md:table-cell">Email</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Role</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary hidden lg:table-cell">Unit</th>
                  <th className="text-left px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Status</th>
                  <th className="text-right px-5 py-3.5 text-[11px] font-bold uppercase tracking-widest text-neutral-text-secondary">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-border/50">
                {users.map((user) => {
                  const roleColor = getRoleColor(user.role?.name);
                  const initials = user.name
                    .trim()
                    .split(/\s+/)
                    .filter(n => n.length > 0 && !n.includes('.') && !n.includes(',')) // Filter out titles like S.T., M.T., etc.
                    .map(n => n[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase() || '??';
                  return (
                    <tr key={user.id} className="group hover:bg-teal-main/[0.02] transition-colors">
                      {/* Name + Avatar */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl ${roleColor.avatar} text-white flex items-center justify-center text-xs font-black shadow-sm flex-shrink-0`}>
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-neutral-text-primary truncate">{user.name}</p>
                            <p className="text-xs text-neutral-text-secondary mt-0.5 md:hidden truncate">{user.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-5 py-4 hidden md:table-cell">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-neutral-text-tertiary flex-shrink-0" />
                          <span className="text-sm text-neutral-text-secondary truncate max-w-[200px]">{user.email}</span>
                        </div>
                      </td>

                      {/* Role Badge */}
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold border px-2.5 py-1 rounded-lg ${roleColor.bg} ${roleColor.text} ${roleColor.border}`}>
                          <Shield className="w-3 h-3" />
                          {user.role?.display_name || '-'}
                        </span>
                      </td>

                      {/* Unit */}
                      <td className="px-5 py-4 hidden lg:table-cell">
                        {user.unit?.name ? (
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-neutral-text-tertiary flex-shrink-0" />
                            <span className="text-sm text-neutral-text-secondary truncate max-w-[180px]">{user.unit.name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-neutral-text-tertiary italic">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-lg border ${user.is_active
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                          {user.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(user)}
                            className="p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 text-neutral-text-secondary transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openReset(user)}
                            className="p-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-neutral-text-secondary transition-colors"
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className={`p-2 rounded-lg transition-colors ${user.is_active
                              ? 'hover:bg-amber-50 hover:text-amber-600 text-amber-500'
                              : 'hover:bg-emerald-50 hover:text-emerald-600 text-emerald-500'
                              }`}
                            title={user.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                          >
                            {user.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-2 rounded-lg hover:bg-rose-50 hover:text-rose-600 text-neutral-text-secondary transition-colors"
                            title="Hapus"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalUsers > 0 && (
              <div className="flex items-center justify-between px-5 py-4 border-t border-neutral-border bg-neutral-bg/50">
                <p className="text-xs text-neutral-text-secondary">
                  Menampilkan <span className="font-bold">{((currentPage - 1) * PER_PAGE) + 1}</span> –{' '}
                  <span className="font-bold">{Math.min(currentPage * PER_PAGE, totalUsers)}</span> dari{' '}
                  <span className="font-bold">{totalUsers}</span> pengguna
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-neutral-border hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = totalPages <= 5 ? i + 1
                      : currentPage <= 3 ? i + 1
                        : currentPage >= totalPages - 2 ? totalPages - 4 + i
                          : currentPage - 2 + i;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-bold transition-colors ${currentPage === page
                          ? 'bg-teal-main text-white shadow-sm'
                          : 'hover:bg-white border border-neutral-border text-neutral-text-secondary'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-neutral-border hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editUser ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSave} loading={saving} icon={<Plus className="w-4 h-4" />}>
              {editUser ? 'Simpan Perubahan' : 'Tambah User'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {editUser && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <div className={`w-10 h-10 rounded-xl ${getRoleColor(editUser.role?.name).avatar} text-white flex items-center justify-center text-sm font-black`}>
                {editUser.name.trim().split(/\s+/).filter(n => n.length > 0 && !n.includes('.') && !n.includes(',')).map(n => n[0]).slice(0, 2).join('').toUpperCase() || '??'}
              </div>
              <div>
                <p className="text-sm font-bold text-neutral-text-primary">{editUser.name}</p>
                <p className="text-xs text-neutral-text-secondary">{editUser.email}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Nama Lengkap" placeholder="Nama pengguna" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            <Input label="Email" type="email" placeholder="email@ubpkarawang.ac.id" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          {!editUser && (
            <Input label="Password" type="password" placeholder="Minimal 8 karakter" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required helpText="Password akan digunakan saat login pertama kali" />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Role" options={roles.map(r => ({ value: r.id.toString(), label: r.display_name }))} placeholder="Pilih role" value={formData.role_id} onChange={(v) => setFormData({ ...formData, role_id: v })} required />
            <Select label="Unit" options={[{ value: '', label: '— Tidak ada unit —' }, ...flatUnitOptions]} placeholder="Pilih unit" value={formData.unit_id} onChange={(v) => setFormData({ ...formData, unit_id: v })} />
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset Password"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowResetModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleResetPassword} loading={resetSaving} icon={<KeyRound className="w-4 h-4" />}>
              Reset Password
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {resetTarget && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <div className={`w-9 h-9 rounded-xl ${getRoleColor(resetTarget.role?.name).avatar} text-white flex items-center justify-center text-sm font-black flex-shrink-0`}>
                {resetTarget.name.trim().split(/\s+/).filter(n => n.length > 0 && !n.includes('.') && !n.includes(',')).map(n => n[0]).slice(0, 2).join('').toUpperCase() || '??'}
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800">{resetTarget.name}</p>
                <p className="text-xs text-amber-600">{resetTarget.email}</p>
              </div>
            </div>
          )}
          <Input
            label="Password Baru"
            type="password"
            placeholder="Minimal 8 karakter"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helpText="Password lama akan diganti setelah disimpan"
            required
          />
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;
